import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Card, CardBody, Container, Spinner } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import DataTableNext from '../../components/Common/DataTable/DataTableNext';
import Modal from '../../components/Common/Modal/Modal';
import ShowSingleElement from '../../components/Common/ShowSingleElement';
import DiscountCodeForm from '../../components/DiscountCodes/DiscountCodeForm';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import createTableColumns from '../../components/DiscountCodes/tableColumns';
import {
  ExportButtonPlacement,
  ToggleColumnButton,
  CreateButton,
} from '../../components/Common/DataTable/tableActionButtons';
import { addOrRemove } from '../../helpers/sharedFunction';
import {
  fetchDiscountCodes,
  deleteDiscountCode,
  resetMessage,
  resetState,
} from '../../store/discountCodes/actions';
import {
  selectDiscountCodeLoading,
  selectDiscountCodeMessage,
} from '../../selectors/discountCode';

const DiscountCodesList = () => {
  const dispatch = useDispatch();

  // Redux selectors
  const discountCodes = useSelector(
    (state) => state.DiscountCode.discountCodes,
  );
  const loading = useSelector(selectDiscountCodeLoading);
  const message = useSelector(selectDiscountCodeMessage);

  // Modal states
  const [viewModal, setViewModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedDiscountCode, setSelectedDiscountCode] = useState(null);

  // Table states
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    dispatch(fetchDiscountCodes());
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (message) {
      setTimeout(() => {
        dispatch(resetMessage());
      }, 3000);
    }
  }, [message, dispatch]);

  const openEditModal = useCallback((discountCode = null) => {
    setSelectedDiscountCode(discountCode);
    setEditModal(true);
  }, []);

  const openViewModal = useCallback((discountCode) => {
    setSelectedDiscountCode(discountCode);
    setViewModal(true);
  }, []);

  const handleDelete = useCallback(
    (discountCode) => {
      if (
        window.confirm('Sei sicuro di voler eliminare questo codice sconto?')
      ) {
        dispatch(deleteDiscountCode(discountCode.id));
      }
    },
    [dispatch],
  );

  const columns = useMemo(
    () => createTableColumns(openViewModal, openEditModal, handleDelete),
    [openViewModal, openEditModal, handleDelete],
  );

  const [hiddenColumns, setHiddenColumns] = useState(
    columns.filter(({ hidden }) => !!hidden).map(({ name }) => name),
  );

  const handleToggleColumn = (name) => {
    setHiddenColumns(addOrRemove(hiddenColumns, name));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Codici Sconto" />
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Row className="align-items-center justify-content-end mb-2">
                    <Spinner
                      color="secondary"
                      animation="border"
                      hidden={!loading}
                    />
                    <CreateButton
                      label="Nuovo Codice Sconto"
                      disabled={loading}
                      onPress={() => openEditModal(null)}
                    />
                    <ToggleColumnButton
                      columns={columns}
                      hiddenColumns={hiddenColumns}
                      onToggleColumn={handleToggleColumn}
                    />
                    <ExportButtonPlacement />
                  </Row>
                  <DataTableNext
                    loading={loading}
                    rows={discountCodes}
                    columns={columns}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    currentPage={currentPage}
                    onCurrentPageChange={setCurrentPage}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal
            size="lg"
            title="Codice Sconto"
            isOpen={viewModal}
            toggle={() => setViewModal(!viewModal)}
            showClose
            scrollable={false}>
            <ShowSingleElement element={selectedDiscountCode} />
          </Modal>
          <Modal
            size="xl"
            title="Codice Sconto"
            isOpen={editModal}
            toggle={() => setEditModal(!editModal)}
            scrollable={false}>
            <DiscountCodeForm
              closeModal={() => setEditModal(false)}
              create={!selectedDiscountCode}
              loading={loading}
              discountCode={selectedDiscountCode}
              onSave={() => setEditModal(false)}
              onCancel={() => setEditModal(false)}
            />
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DiscountCodesList;
